// client/src/components/leads/b2c/SendEmailModal.js
// PROFESSIONAL EMAIL MODAL - HubSpot Style
// Features: Template library, placeholder system, preview, Digital Trent branding

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import apiClient from "../../../services/api";

export default function SendEmailModal({ segment = "all", customers = [], onClose, onEmailSent }) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('compose'); // compose, preview, settings
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  const [formData, setFormData] = useState({
    from: "Digital Trent <noreply@digitaltrent.co.uk>",
    subject: "",
    htmlBody: "",
    previewText: ""
  });

  // Available email templates
  const emailTemplates = {
    winback: {
      name: "Win-Back Campaign",
      icon: "fa-heart-broken",
      color: "red",
      subject: "We Miss You, {{firstName}}! 💙",
      previewText: "Special offer just for you",
      htmlBody: `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 40px 20px;">
  <!-- Header -->
  <div style="text-align: center; margin-bottom: 40px;">
    <div style="background: #2563eb; color: white; width: 60px; height: 60px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 28px; margin-bottom: 20px;">
      💙
    </div>
    <h1 style="color: #111827; font-size: 28px; font-weight: 700; margin: 0;">We Miss You!</h1>
  </div>

  <!-- Body -->
  <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 32px; margin-bottom: 24px;">
    <p style="color: #111827; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Hi {{firstName}},</p>
    
    <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
      We noticed it's been a while since your last visit on <strong>{{lastVisit}}</strong>. We'd love to see you again!
    </p>
    
    <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 24px 0;">
      To welcome you back, we're offering you an exclusive <strong>20% discount</strong> on your next visit.
    </p>
    
    <!-- CTA Button -->
    <div style="text-align: center; margin: 32px 0;">
      <a href="{{bookingLink}}" style="display: inline-block; background: #2563eb; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 8px; font-weight: 600; font-size: 16px;">
        Book Your Appointment
      </a>
    </div>
    
    <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 24px 0 0 0; text-align: center;">
      Offer valid for 14 days
    </p>
  </div>

  <!-- Footer -->
  <div style="text-align: center; padding-top: 24px; border-top: 1px solid #e5e7eb;">
    <p style="color: #6b7280; font-size: 14px; margin: 0 0 8px 0;">
      <strong>{{businessName}}</strong>
    </p>
    <p style="color: #9ca3af; font-size: 12px; margin: 0 0 16px 0;">
      {{businessAddress}} · {{businessPhone}}
    </p>
    <p style="color: #9ca3af; font-size: 11px; margin: 0;">
      <a href="{{unsubscribeLink}}" style="color: #9ca3af; text-decoration: underline;">Unsubscribe</a>
    </p>
  </div>
</div>`
    },
    
    vip: {
      name: "VIP Appreciation",
      icon: "fa-star",
      color: "yellow",
      subject: "Thank You for Being a VIP Customer! ⭐",
      previewText: "Exclusive perks for our best customers",
      htmlBody: `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 40px 20px;">
  <!-- Header with VIP Badge -->
  <div style="text-align: center; margin-bottom: 40px;">
    <div style="background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%); color: white; width: 80px; height: 80px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 36px; margin-bottom: 20px; box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);">
      ⭐
    </div>
    <h1 style="color: #111827; font-size: 28px; font-weight: 700; margin: 0;">You're a VIP!</h1>
    <p style="color: #6b7280; font-size: 14px; margin: 8px 0 0 0;">Our Most Valued Customer</p>
  </div>

  <!-- Body -->
  <div style="background: #ffffff; border: 2px solid #fbbf24; border-radius: 12px; padding: 32px; margin-bottom: 24px;">
    <p style="color: #111827; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Dear {{name}},</p>
    
    <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
      We wanted to take a moment to thank you for being one of our most loyal customers. You've visited us <strong>{{visitCount}} times</strong> and we truly appreciate your continued support!
    </p>
    
    <!-- VIP Benefits -->
    <div style="background: #fffbeb; border-left: 4px solid #fbbf24; padding: 20px; margin: 24px 0; border-radius: 8px;">
      <h3 style="color: #92400e; font-size: 16px; font-weight: 600; margin: 0 0 12px 0;">Your VIP Benefits:</h3>
      <ul style="color: #78350f; font-size: 14px; line-height: 1.8; margin: 0; padding-left: 20px;">
        <li>Priority booking</li>
        <li>Exclusive first access to new services</li>
        <li>Special birthday treat</li>
        <li>15% discount on all services</li>
      </ul>
    </div>
    
    <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 24px 0 0 0;">
      Thank you for choosing us. We look forward to serving you again soon!
    </p>
  </div>

  <!-- Footer -->
  <div style="text-align: center; padding-top: 24px; border-top: 1px solid #e5e7eb;">
    <p style="color: #6b7280; font-size: 14px; margin: 0 0 8px 0;">
      <strong>{{businessName}}</strong>
    </p>
    <p style="color: #9ca3af; font-size: 12px; margin: 0 0 16px 0;">
      {{businessAddress}} · {{businessPhone}}
    </p>
    <p style="color: #9ca3af; font-size: 11px; margin: 0;">
      <a href="{{unsubscribeLink}}" style="color: #9ca3af; text-decoration: underline;">Unsubscribe</a>
    </p>
  </div>
</div>`
    },
    
    welcome: {
      name: "Welcome New Customer",
      icon: "fa-gift",
      color: "green",
      subject: "Welcome to {{businessName}}! 🎉",
      previewText: "Here's a special gift to get you started",
      htmlBody: `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 40px 20px;">
  <!-- Header -->
  <div style="text-align: center; margin-bottom: 40px;">
    <div style="background: #16a34a; color: white; width: 60px; height: 60px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 28px; margin-bottom: 20px;">
      🎉
    </div>
    <h1 style="color: #111827; font-size: 28px; font-weight: 700; margin: 0;">Welcome!</h1>
  </div>

  <!-- Body -->
  <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 32px; margin-bottom: 24px;">
    <p style="color: #111827; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Hi {{firstName}},</p>
    
    <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
      Welcome to <strong>{{businessName}}</strong>! We're thrilled to have you as a new customer.
    </p>
    
    <!-- Welcome Gift -->
    <div style="background: #f0fdf4; border: 2px dashed #16a34a; border-radius: 12px; padding: 24px; margin: 24px 0; text-align: center;">
      <div style="font-size: 48px; margin-bottom: 12px;">🎁</div>
      <h3 style="color: #166534; font-size: 20px; font-weight: 600; margin: 0 0 8px 0;">Your Welcome Gift</h3>
      <p style="color: #15803d; font-size: 24px; font-weight: 700; margin: 0;">15% OFF</p>
      <p style="color: #16a34a; font-size: 14px; margin: 8px 0 0 0;">Your First Visit</p>
    </div>
    
    <!-- CTA Button -->
    <div style="text-align: center; margin: 32px 0;">
      <a href="{{bookingLink}}" style="display: inline-block; background: #16a34a; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 8px; font-weight: 600; font-size: 16px;">
        Book Your First Visit
      </a>
    </div>
    
    <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 24px 0 0 0;">
      We can't wait to see you! If you have any questions, feel free to reply to this email.
    </p>
  </div>

  <!-- Footer -->
  <div style="text-align: center; padding-top: 24px; border-top: 1px solid #e5e7eb;">
    <p style="color: #6b7280; font-size: 14px; margin: 0 0 8px 0;">
      <strong>{{businessName}}</strong>
    </p>
    <p style="color: #9ca3af; font-size: 12px; margin: 0 0 16px 0;">
      {{businessAddress}} · {{businessPhone}}
    </p>
    <p style="color: #9ca3af; font-size: 11px; margin: 0;">
      <a href="{{unsubscribeLink}}" style="color: #9ca3af; text-decoration: underline;">Unsubscribe</a>
    </p>
  </div>
</div>`
    },
    
    reminder: {
      name: "Appointment Reminder",
      icon: "fa-calendar",
      color: "blue",
      subject: "Time for Your Next Visit? 📅",
      previewText: "We'd love to see you again soon",
      htmlBody: `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 40px 20px;">
  <!-- Header -->
  <div style="text-align: center; margin-bottom: 40px;">
    <div style="background: #2563eb; color: white; width: 60px; height: 60px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 28px; margin-bottom: 20px;">
      📅
    </div>
    <h1 style="color: #111827; font-size: 28px; font-weight: 700; margin: 0;">It's Time!</h1>
  </div>

  <!-- Body -->
  <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 32px; margin-bottom: 24px;">
    <p style="color: #111827; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Hi {{firstName}},</p>
    
    <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
      Based on your usual schedule, you're due for your next visit around <strong>{{nextVisit}}</strong>.
    </p>
    
    <!-- Time Info Box -->
    <div style="background: #eff6ff; border-left: 4px solid #2563eb; padding: 20px; margin: 24px 0; border-radius: 8px;">
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="font-size: 32px;">⏰</div>
        <div>
          <p style="color: #1e40af; font-size: 14px; font-weight: 600; margin: 0 0 4px 0;">Last Visit</p>
          <p style="color: #3b82f6; font-size: 16px; margin: 0;">{{lastVisit}}</p>
        </div>
      </div>
    </div>
    
    <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 24px 0;">
      We have availability this week and would love to see you!
    </p>
    
    <!-- CTA Button -->
    <div style="text-align: center; margin: 32px 0;">
      <a href="{{bookingLink}}" style="display: inline-block; background: #2563eb; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 8px; font-weight: 600; font-size: 16px;">
        Book Your Appointment
      </a>
    </div>
  </div>

  <!-- Footer -->
  <div style="text-align: center; padding-top: 24px; border-top: 1px solid #e5e7eb;">
    <p style="color: #6b7280; font-size: 14px; margin: 0 0 8px 0;">
      <strong>{{businessName}}</strong>
    </p>
    <p style="color: #9ca3af; font-size: 12px; margin: 0 0 16px 0;">
      {{businessAddress}} · {{businessPhone}}
    </p>
    <p style="color: #9ca3af; font-size: 11px; margin: 0;">
      <a href="{{unsubscribeLink}}" style="color: #9ca3af; text-decoration: underline;">Unsubscribe</a>
    </p>
  </div>
</div>`
    },
    
    blank: {
      name: "Blank Template",
      icon: "fa-file",
      color: "gray",
      subject: "",
      previewText: "",
      htmlBody: `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 40px 20px;">
  <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 32px;">
    <p style="color: #111827; font-size: 16px; line-height: 1.6;">Hi {{firstName}},</p>
    
    <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
      Your message here...
    </p>
  </div>

  <!-- Footer -->
  <div style="text-align: center; padding-top: 24px; border-top: 1px solid #e5e7eb; margin-top: 24px;">
    <p style="color: #6b7280; font-size: 14px; margin: 0 0 8px 0;">
      <strong>{{businessName}}</strong>
    </p>
    <p style="color: #9ca3af; font-size: 12px; margin: 0 0 16px 0;">
      {{businessAddress}} · {{businessPhone}}
    </p>
    <p style="color: #9ca3af; font-size: 11px; margin: 0;">
      <a href="{{unsubscribeLink}}" style="color: #9ca3af; text-decoration: underline;">Unsubscribe</a>
    </p>
  </div>
</div>`
    }
  };

  // Available placeholder variables
  const availablePlaceholders = [
    { var: '{{name}}', desc: 'Full customer name', example: 'Sarah Johnson' },
    { var: '{{firstName}}', desc: 'First name only', example: 'Sarah' },
    { var: '{{email}}', desc: 'Email address', example: 'sarah@email.com' },
    { var: '{{phone}}', desc: 'Phone number', example: '+44 7700 123456' },
    { var: '{{lastVisit}}', desc: 'Date of last visit', example: '15 Nov 2024' },
    { var: '{{nextVisit}}', desc: 'Expected next visit', example: '20 Dec 2024' },
    { var: '{{visitCount}}', desc: 'Total visits', example: '12' },
    { var: '{{lifetimeSpend}}', desc: 'Total spent', example: '£450.00' },
    { var: '{{businessName}}', desc: 'Your business name', example: 'Bella Beauty Salon' },
    { var: '{{businessAddress}}', desc: 'Business address', example: '123 High Street, London' },
    { var: '{{businessPhone}}', desc: 'Business phone', example: '+44 20 1234 5678' },
    { var: '{{bookingLink}}', desc: 'Booking page URL', example: 'https://example.com/book' },
    { var: '{{unsubscribeLink}}', desc: 'Unsubscribe link', example: 'Auto-generated' }
  ];

  useEffect(() => {
    // Auto-select template based on segment
    const segmentTemplateMap = {
      'dormant': 'winback',
      'at-risk': 'winback',
      'vip': 'vip',
      'new': 'welcome',
      'frequent': 'reminder',
      'regular': 'reminder'
    };
    
    const templateKey = segmentTemplateMap[segment] || 'blank';
    handleSelectTemplate(templateKey);
  }, [segment]);

  const handleSelectTemplate = (templateKey) => {
    const template = emailTemplates[templateKey];
    if (template) {
      setSelectedTemplate(templateKey);
      setFormData({
        ...formData,
        subject: template.subject,
        htmlBody: template.htmlBody,
        previewText: template.previewText
      });
    }
  };

  const insertPlaceholder = (placeholder) => {
    setFormData({
      ...formData,
      htmlBody: formData.htmlBody + placeholder
    });
  };

  const handleSend = async () => {
    if (!formData.subject.trim() || !formData.htmlBody.trim()) {
      alert("Please fill in subject and email body");
      return;
    }

    const eligibleCustomers = customers.filter(
      (c) => c && c.email && !c.emailOptOut && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)
    );

    if (eligibleCustomers.length === 0) {
      alert("No valid email recipients in this segment.");
      return;
    }

    if (!window.confirm(`Send email to ${eligibleCustomers.length} customers?`)) {
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post("/customers/communication/send-segment-email", {
        segment: segment || "all",
        subject: formData.subject,
        htmlBody: formData.htmlBody,
        previewText: formData.previewText
      });

      const data = response.data;

      if (data.success) {
        alert(`✓ Successfully sent ${data.sent?.length || 0} emails`);
        onEmailSent();
        onClose();
      } else {
        alert(`Error: ${data.error || 'Failed to send emails'}`);
      }
    } catch (error) {
      console.error('Send email error:', error);
      alert("Failed to send emails. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const eligibleCount = customers.filter(
    (c) => c && c.email && !c.emailOptOut && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)
  ).length;

  const ineligibleCount = customers.length - eligibleCount;

  const getTemplateColor = (color) => {
    const colors = {
      red: 'border-red-200 bg-red-50 hover:bg-red-100',
      yellow: 'border-yellow-200 bg-yellow-50 hover:bg-yellow-100',
      green: 'border-green-200 bg-green-50 hover:bg-green-100',
      blue: 'border-blue-200 bg-blue-50 hover:bg-blue-100',
      gray: 'border-gray-200 bg-gray-50 hover:bg-gray-100'
    };
    return colors[color] || colors.gray;
  };

  const getTemplateIconColor = (color) => {
    const colors = {
      red: 'text-red-600',
      yellow: 'text-yellow-600',
      green: 'text-green-600',
      blue: 'text-blue-600',
      gray: 'text-gray-600'
    };
    return colors[color] || colors.gray;
  };

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed right-0 top-0 w-full max-w-4xl h-full bg-white shadow-2xl z-50 overflow-y-auto animate-slide-in">
        
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-300 p-6 z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">Send Email Campaign</h2>
              <p className="text-sm text-gray-600 mt-1">
                <i className="fas fa-users mr-2"></i>
                Segment: <span className="font-semibold capitalize">{segment}</span>
                <span className="mx-2">·</span>
                <span className="text-green-600 font-semibold">{eligibleCount} recipients</span>
                {ineligibleCount > 0 && (
                  <span className="text-gray-500 text-xs ml-2">
                    ({ineligibleCount} excluded)
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 border-b border-gray-200 -mb-px">
            <button
              onClick={() => setActiveTab('compose')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'compose'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="fas fa-edit mr-2"></i>
              Compose
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'preview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="fas fa-eye mr-2"></i>
              Preview
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'settings'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="fas fa-cog mr-2"></i>
              Settings
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">

          {/* COMPOSE TAB */}
          {activeTab === 'compose' && (
            <div className="space-y-6">
              
              {/* Template Library */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  <i className="fas fa-layer-group mr-2"></i>
                  Email Templates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Object.entries(emailTemplates).map(([key, template]) => (
                    <button
                      key={key}
                      onClick={() => handleSelectTemplate(key)}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        selectedTemplate === key
                          ? 'border-blue-600 bg-blue-50'
                          : getTemplateColor(template.color)
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <i className={`fas ${template.icon} text-2xl ${getTemplateIconColor(template.color)}`}></i>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {template.name}
                          </div>
                          <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                            {template.previewText || template.subject}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Email Details */}
              <div className="space-y-4">
                
                {/* From */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <i className="fas fa-user mr-2"></i>
                    From
                  </label>
                  <input
                    type="text"
                    value={formData.from}
                    onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="Your Name <email@digitaltrent.co.uk>"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <i className="fas fa-heading mr-2"></i>
                    Subject Line *
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="e.g., We Miss You, {{firstName}}!"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    <i className="fas fa-lightbulb mr-1"></i>
                    Use placeholders like {'{{firstName}}'} for personalization
                  </p>
                </div>

                {/* Preview Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <i className="fas fa-align-left mr-2"></i>
                    Preview Text (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.previewText}
                    onChange={(e) => setFormData({ ...formData, previewText: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="Text shown in email inbox preview"
                  />
                </div>

                {/* Email Body */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                      <i className="fas fa-file-code mr-2"></i>
                      Email Body (HTML) *
                    </label>
                    <button
                      onClick={() => setActiveTab('preview')}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <i className="fas fa-eye mr-1"></i>
                      Preview Email
                    </button>
                  </div>
                  <textarea
                    rows={16}
                    value={formData.htmlBody}
                    onChange={(e) => setFormData({ ...formData, htmlBody: e.target.value })}
                    className="w-full px-3 py-2 text-xs font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="<div>Your HTML email content here...</div>"
                  />
                </div>

                {/* Placeholder Helper */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-blue-900 mb-2">
                        Available Placeholders
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                        {availablePlaceholders.slice(0, 6).map((p) => (
                          <button
                            key={p.var}
                            onClick={() => insertPlaceholder(p.var)}
                            className="flex items-center gap-2 text-left px-2 py-1.5 bg-white border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                          >
                            <code className="text-blue-600 font-mono font-semibold">{p.var}</code>
                            <span className="text-gray-600 text-xs">- {p.desc}</span>
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setActiveTab('settings')}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-2"
                      >
                        View all placeholders →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PREVIEW TAB */}
          {activeTab === 'preview' && (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  <i className="fas fa-envelope mr-2"></i>
                  Email Preview
                </h3>
                <p className="text-xs text-gray-600">
                  This is how your email will appear in recipients' inboxes. Placeholders shown with example data.
                </p>
              </div>

              {/* Email Preview Container */}
              <div className="bg-gray-100 p-6 rounded-lg">
                <div className="max-w-2xl mx-auto bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                  {/* Email Header (Inbox View) */}
                  <div className="bg-gray-50 border-b border-gray-200 p-4">
                    <div className="text-xs text-gray-600 mb-1">From: {formData.from || 'Digital Trent'}</div>
                    <div className="text-sm font-semibold text-gray-900">
                      {formData.subject.replace(/\{\{firstName\}\}/g, 'Sarah').replace(/\{\{name\}\}/g, 'Sarah Johnson') || 'No subject'}
                    </div>
                    {formData.previewText && (
                      <div className="text-xs text-gray-500 mt-1">{formData.previewText}</div>
                    )}
                  </div>

                  {/* Email Body Preview */}
                  <div 
                    className="p-6"
                    dangerouslySetInnerHTML={{
                      __html: formData.htmlBody
                        .replace(/\{\{name\}\}/g, 'Sarah Johnson')
                        .replace(/\{\{firstName\}\}/g, 'Sarah')
                        .replace(/\{\{email\}\}/g, 'sarah@email.com')
                        .replace(/\{\{phone\}\}/g, '+44 7700 123456')
                        .replace(/\{\{lastVisit\}\}/g, '15 November 2024')
                        .replace(/\{\{nextVisit\}\}/g, '20 December 2024')
                        .replace(/\{\{visitCount\}\}/g, '12')
                        .replace(/\{\{lifetimeSpend\}\}/g, '£450.00')
                        .replace(/\{\{businessName\}\}/g, 'Your Business Name')
                        .replace(/\{\{businessAddress\}\}/g, '123 High Street, London')
                        .replace(/\{\{businessPhone\}\}/g, '+44 20 1234 5678')
                        .replace(/\{\{bookingLink\}\}/g, 'https://example.com/book')
                        .replace(/\{\{unsubscribeLink\}\}/g, '#unsubscribe')
                    }}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setActiveTab('compose')}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Compose
                </button>
                <button
                  onClick={handleSend}
                  disabled={loading || eligibleCount === 0}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-2"></i>
                      Send to {eligibleCount} Recipients
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              
              {/* All Placeholders */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  <i className="fas fa-code mr-2"></i>
                  All Available Placeholders
                </h3>
                <div className="bg-white border border-gray-300 rounded-lg divide-y divide-gray-200">
                  {availablePlaceholders.map((placeholder) => (
                    <div key={placeholder.var} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <code className="text-sm font-mono font-semibold text-blue-600">{placeholder.var}</code>
                          <p className="text-sm text-gray-600 mt-1">{placeholder.desc}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            <i className="fas fa-arrow-right mr-1"></i>
                            Example: {placeholder.example}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(placeholder.var);
                            alert('Copied to clipboard!');
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <i className="fas fa-copy mr-1"></i>
                          Copy
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sending Info */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-yellow-900 mb-2">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  Email Sending Guidelines
                </h4>
                <ul className="text-sm text-yellow-800 space-y-1.5">
                  <li>• Emails are sent from: <strong>noreply@digitaltrent.co.uk</strong></li>
                  <li>• Rate limited to prevent spam flags (100ms delay between emails)</li>
                  <li>• Customers who opted out or have invalid emails will be skipped</li>
                  <li>• All emails include automatic unsubscribe link</li>
                  <li>• Personalization happens automatically using customer data</li>
                </ul>
              </div>

              {/* Best Practices */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                  <i className="fas fa-lightbulb mr-2"></i>
                  Email Best Practices
                </h4>
                <ul className="text-sm text-blue-800 space-y-1.5">
                  <li>• Use personalization ({'{{firstName}}'}) to increase engagement</li>
                  <li>• Keep subject lines under 50 characters for mobile</li>
                  <li>• Include clear call-to-action buttons</li>
                  <li>• Test on mobile devices before sending</li>
                  <li>• Send during business hours for better open rates</li>
                </ul>
              </div>
            </div>
          )}

        </div>

        {/* Footer Actions */}
        {activeTab !== 'preview' && (
          <div className="sticky bottom-0 bg-white border-t border-gray-300 p-6">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className="flex-1 px-4 py-2.5 bg-white text-gray-700 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i className="fas fa-eye mr-2"></i>
                Preview
              </button>
              <button
                onClick={handleSend}
                disabled={loading || eligibleCount === 0}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2"></i>
                    Send to {eligibleCount}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

      </div>

      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>,
    document.body
  );
}